import { loadCaseLabels, loadCategoryToShort } from '@editorUtils'
import type {
  ComboPointLoadDTO,
  DesignForceBreakdownDTO,
  DesignForcesOnSupportDTO,
} from '@api/fastAPI.schemas'
import { formulaToNumber } from '@utils'

/** Draft state of one manual load row (formula kept as entered, in kN). */
export interface AnchorManualLoadDraft {
  source: string
  formula: string
}

export type AnchorLoadCategory = 'Wind' | 'EarthQuake'

export const anchorLoadCategoryLabel: Record<AnchorLoadCategory, string> = {
  Wind: 'Wind',
  EarthQuake: 'Erdbeben',
}

const knFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Formats a force in Newtons as kN with 2 decimals (de-DE). */
export const formatNewtonsAsKn = (newtons: number | undefined | null): string =>
  newtons == null ? '–' : knFormatter.format(newtons / 1000)

/** Bounds (in Newtons) of the F_d,neu range the anchor list marks as uncritical. */
const UNCRITICAL_ANCHOR_DESIGN_FORCE_MIN = 0
const UNCRITICAL_ANCHOR_DESIGN_FORCE_MAX = 3000

/**
 * True for a small remaining design tensile load (0 … 3 kN) — highlighted in the anchor list.
 * A negative F_d,neu is deliberately outside the range.
 */
export const isUncriticalAnchorDesignForce = (newtons: number): boolean =>
  newtons >= UNCRITICAL_ANCHOR_DESIGN_FORCE_MIN && newtons <= UNCRITICAL_ANCHOR_DESIGN_FORCE_MAX

/** Formats a length in meters with 2 decimals (de-DE). */
export const formatMeters = (meters: number | undefined | null): string =>
  meters == null ? '–' : `${knFormatter.format(meters)} m`

/** Formats a plain number (e.g. a utilization) with 2 decimals (de-DE). */
export const formatNumber = (value: number | undefined | null): string =>
  value == null ? '–' : knFormatter.format(value)

/** `formulaToNumber` that reports invalid formulas as `null` instead of throwing. */
export const tryFormulaToNumber = (formula: string): number | null => {
  if (!formula.trim()) return null
  try {
    return formulaToNumber(formula)
  } catch {
    return null
  }
}

/** Sum of all valid manual load rows in Newtons (formulas are entered in kN). */
export const manualLoadDraftSum = (drafts: AnchorManualLoadDraft[]): number =>
  drafts.reduce((sum, draft) => {
    if (!draft.source || !draft.formula) return sum
    const value = tryFormulaToNumber(draft.formula)
    return value == null ? sum : sum + value * 1000
  }, 0)

/** Complete draft rows as the persisted manual loads (formulas are entered in kN, stored in N). */
export const draftsToManualLoads = (drafts: AnchorManualLoadDraft[]): ManualLoad[] =>
  drafts
    .filter(draft => draft.source && draft.formula.trim())
    .map(draft => ({
      load: (tryFormulaToNumber(draft.formula) ?? 0) * 1000,
      load_formula: draft.formula,
      source: draft.source,
    }))

/** True when a row is partially filled or holds an unparsable formula (blocks saving). */
export const hasInvalidManualLoadDraft = (drafts: AnchorManualLoadDraft[]): boolean =>
  drafts.some(draft => {
    const hasSource = !!draft.source
    const hasFormula = !!draft.formula.trim()
    if (hasSource !== hasFormula) return true
    return hasFormula && tryFormulaToNumber(draft.formula) == null
  })

interface LoadCaseLike {
  category?: string
  load_case_type?: string
}

/** Unwraps a `LoadCaseDTO` or `SubLoadCaseDTO` (which nests the load case) to its load case. */
const unwrapLoadCase = (value: unknown): LoadCaseLike | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const candidate = value as LoadCaseLike & { load_case?: LoadCaseLike }
  return candidate.load_case ?? candidate
}

/** Short label of a load case's category ("G", "L", "S", "W", "E") — "-" when unknown. */
export const formatLoadCategoryShort = (loadCase: unknown): string => {
  const unwrapped = unwrapLoadCase(loadCase)
  if (!unwrapped?.category) return '-'
  return loadCategoryToShort[unwrapped.category as LoadCategory] ?? '-'
}

/** Short German label of a load case ("W 0°", "E 0°", …) with the raw type as fallback. */
export const formatLoadCase = (loadCase: unknown, fallback: string): string => {
  const unwrapped = unwrapLoadCase(loadCase)
  if (!unwrapped?.category || !unwrapped?.load_case_type) return fallback
  const labels = loadCaseLabels[unwrapped.category as LoadCategory]
  return labels?.[unwrapped.load_case_type as LoadCaseTypes]?.short ?? fallback
}

/**
 * Stable guid of an Einwirkungen load — used to match the load table rows with the scene loads
 * (e.g. for hiding individual loads). Falls back to a synthesized per-support key when the
 * original load carries no guid.
 */
export const anchorLoadGuid = (
  originalLoad: { guid?: string | null },
  supportGuid: string,
  kind: string,
  index: number,
): string => originalLoad.guid ?? `${supportGuid}-${kind}-${index}`

/**
 * Maps the relieving (compressing) point/line loads of a support to scene `DomainLoad`s so they
 * can be rendered with `SimpleLoadMesh` while the anchor is selected. The DTO carries the full
 * original loads; the domain is resolved via `guidToDomain` and loads without a resolvable
 * domain or load case are skipped.
 */
export const getReducingDomainLoads = (
  designForces: DesignForcesOnSupportDTO | undefined,
  guidToDomain: Record<string, Domain | undefined>,
  elementGuid: string,
): DomainLoad[] => {
  if (!designForces) return []

  const toLoadCase = (value: unknown): LoadCase | undefined => {
    const unwrapped = unwrapLoadCase(value)
    if (!unwrapped?.category || !unwrapped.load_case_type) return undefined
    return {
      identifier: unwrapped.load_case_type,
      category: unwrapped.category as LoadCategory,
      load_case_type: unwrapped.load_case_type as LoadCaseTypes,
    }
  }

  const loads: DomainLoad[] = []

  designForces.reducing_point_loads.forEach((contribution, index) => {
    const dto = contribution.original_load
    const domain = dto.domain_guid ? guidToDomain[dto.domain_guid] : undefined
    const loadCase = toLoadCase(dto.load_case)
    if (!domain || !loadCase) return
    loads.push({
      guid: anchorLoadGuid(dto, designForces.support_guid, 'reducing-point', index),
      element_guid: elementGuid,
      load_type: 'point-load',
      load_case: loadCase,
      force: dto.force,
      relative_position: Number(dto.relative_position),
      domain_guid: domain.guid,
      domain,
    })
  })

  designForces.reducing_line_loads.forEach((contribution, index) => {
    const dto = contribution.original_load
    const domain = dto.domain_guid ? guidToDomain[dto.domain_guid] : undefined
    const loadCase = toLoadCase(dto.load_case)
    if (!domain || !loadCase) return
    loads.push({
      guid: anchorLoadGuid(dto, designForces.support_guid, 'reducing-line', index),
      element_guid: elementGuid,
      load_type: 'line-load',
      load_case: loadCase,
      start: dto.start,
      end: dto.end,
      relative_start: Number(dto.relative_start ?? 0),
      relative_end: Number(dto.relative_end ?? 1),
      domain_guid: domain.guid,
      domain,
    })
  })

  return loads
}

/**
 * Per-category anchor prediction shown while the manual loads are edited — delivered by the
 * backend's `anchor-check-preview` endpoint (which reuses the anchor-check pipeline).
 */
export interface PredictedAnchorCheck {
  connection: Connection
  /** Design resistance of the predicted product for the category, in Newtons. */
  resistance: number
  utilization: number
}

/** Wind/Erdbeben category of a load-case combination (earthquake wins when present). */
export const getCombinationCategory = (
  combination: ComboPointLoadDTO['load_combination'] | undefined,
): AnchorLoadCategory =>
  combination?.data?.some(entry => unwrapLoadCase(entry[2])?.category === 'EarthQuake')
    ? 'EarthQuake'
    : 'Wind'

/**
 * Decisive (max tensile) design force per Wind/Erdbeben category from the backend's
 * per-support design forces (manual loads not included), as the backend's breakdown into
 * the tensile and the stabilizing part.
 */
export const getDesignForcePerCategory = (
  designForces: DesignForcesOnSupportDTO | undefined,
): Partial<Record<AnchorLoadCategory, DesignForceBreakdownDTO>> => {
  const result: Partial<Record<AnchorLoadCategory, DesignForceBreakdownDTO>> = {}
  for (const breakdown of designForces?.design_force_breakdowns ?? []) {
    const category = getCombinationCategory(breakdown.load_combination)
    const current = result[category]
    if (!current || breakdown.total > current.total) result[category] = breakdown
  }
  return result
}
